import React, { useEffect, useMemo, useRef, useState } from "react";
import type { Zone } from "../../../../types";
import { fetchAllCityZones } from "../../../../api/cityZones";
import { Checkbox, Alert, AlertTitle, Spinner, Heading } from "@ogt/ui";

interface CityZoneSectionProps {
  selectedCityZones: Zone[];
  activeCityZoneId: string | null;
  onAddAndActivateCityZone: (zone: Zone) => void;
  onCityZoneRemove: (zoneId: string) => void;
  setActiveCityZoneId: (zoneId: string | null) => void;
}

export function CityZoneSection({
  selectedCityZones,
  activeCityZoneId,
  onAddAndActivateCityZone,
  onCityZoneRemove,
  setActiveCityZoneId,
}: CityZoneSectionProps) {
  const [allCityZones, setAllCityZones] = useState<Zone[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function activate(zone: Zone) {
    onAddAndActivateCityZone(zone);
    setActiveCityZoneId(zone.id);
  }

  const selectedIds = useMemo(
    () => new Set(selectedCityZones.map((z) => z.id)),
    [selectedCityZones]
  );

  const activeHistoryRef = useRef<string[]>([]);

  useEffect(() => {
    const hist = activeHistoryRef.current;
    if (!activeCityZoneId) return;
    activeHistoryRef.current = [
      activeCityZoneId,
      ...hist.filter((id) => id !== activeCityZoneId),
    ];
  }, [activeCityZoneId]);

  useEffect(() => {
    const set = selectedIds;
    activeHistoryRef.current = activeHistoryRef.current.filter((id) =>
      set.has(id)
    );
  }, [selectedIds]);

  useEffect(() => {
    setLoading(true);
    fetchAllCityZones()
      .then((zones) => setAllCityZones(zones))
      .catch((err) => {
        setError("Kunde inte ladda stadszoner");
      })
      .finally(() => setLoading(false));
  }, []);

  function handleCheckedChange(zone: Zone, checked: boolean) {
    if (checked) {
      activate(zone);
    } else {
      if (activeCityZoneId === zone.id) {
        const nextId =
          selectedCityZones.find((z) => z.id !== zone.id)?.id ?? null;
        if (nextId) {
          const nextZone = selectedCityZones.find((z) => z.id === nextId)!;
          activate(nextZone);
        } else {
          setActiveCityZoneId(null);
        }
      }
      onCityZoneRemove(zone.id);
    }
  }

  if (loading)
    return (
      <div className="p-4 pb-6 pt-0">
        Laddar stadszoner… <Spinner></Spinner>
      </div>
    );
  if (error)
    return (
      <div className="p-4 pb-6 pt-0">
        <Alert
          showIcon
          onDismiss={() => {}}
          onOpenChange={() => {}}
          variant="error"
        >
          <AlertTitle>{error}</AlertTitle>
        </Alert>
      </div>
    );

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 pb-6 pt-0">
        <Heading>Stadszoner</Heading>
        <p>Här ser du hur våra stadszoner ser ut.</p>
        <ul className="!list-none !pl-0 mb-4">
          {allCityZones.map((z) => {
            const isSelected = selectedIds.has(z.id);
            const isActive = z.id === activeCityZoneId;

            return (
              <li key={z.id} className="mb-1">
                <Checkbox
                  className="interactive"
                  withLabel
                  id={`city-zone-${z.id}-toggle`}
                  checked={isSelected}
                  onCheckedChange={(checked) =>
                    handleCheckedChange(z, checked === true)
                  }
                >
                  <span
                    className={`cursor-pointer ${
                      isActive ? "font-bold underline" : "font-normal"
                    }`}
                    title={
                      isSelected
                        ? "Klicka för att markera zon"
                        : "Markera rutan för att välja"
                    }
                  >
                    {z.name}
                  </span>
                </Checkbox>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
